<script>
import Layout from "@/router/layouts/main";
import { api } from "@/state/services";
import { MEDALHAS, fmtPeriodo, iniciais } from "@/utils/ranking";
import { RANKINGS_NACIONAIS, fmtIndicador } from "@/utils/ranking-nacional";
import { RANKINGS_ESTADUAIS, contextoEstadual, carregarContextoEstadual } from "@/utils/ranking-estadual";
import { isMenuChildVisible, isMenuGroupVisible } from "@/utils/menu-map";

// Mesmo polling da tela do ranking: o 1º cálculo do período no servidor responde 202 "processing".
const POLL_INTERVALO_MS = 4000;
const POLL_MAX_TENTATIVAS = 45;
// São até 18 rankings: poucos de cada vez para não disparar o cálculo de todos ao mesmo tempo.
const SIMULTANEOS = 3;

const SECOES = [
  { key: "nacional", label: "Ranking Nacional", rankings: RANKINGS_NACIONAIS },
  { key: "estadual", label: "Ranking Estadual", rankings: RANKINGS_ESTADUAIS },
];
const GRUPOS = [
  { key: "brasil", label: "Brasil" },
  { key: "truck", label: "Truck" },
];

/**
 * Resumo de todos os rankings (/biteams/rk-resumo): Ranking Nacional e Ranking Estadual, cada um
 * separado em Brasil / Truck, com um card por ranking (pódio + sua posição + link para a tela
 * completa). Página oculta: não tem item no menu, abre só pela URL.
 *
 * Cada card segue a MESMA regra do menu lateral (utils/menu-map.js): só aparece se o subitem do
 * ranking estiver liberado para o usuário (secretária pela permissão, consultor sem rankings de
 * Regional, Estadual só para quem pertence a uma diretoria).
 */
export default {
  components: { Layout },
  data() {
    return {
      MEDALHAS,
      // id do ranking → { estado: 'carregando'|'processando'|'ok'|'erro'|'sem_diretoria', dados }
      cards: {},
    };
  },
  computed: {
    user() {
      return this.$store.state.account.user;
    },
    secoes() {
      return SECOES.map((s) => ({
        ...s,
        grupos: GRUPOS.filter((g) => isMenuGroupVisible(this.user, "rankings", `${s.key}-${g.key}`))
          .map((g) => ({
            ...g,
            itens: s.rankings.filter((r) => r.grupo === g.key && isMenuChildVisible(this.user, "rankings", r.id)),
          }))
          .filter((g) => g.itens.length),
      })).filter((s) => s.grupos.length);
    },
    visiveis() {
      return this.secoes.reduce((acc, s) => acc.concat(...s.grupos.map((g) => g.itens)), []);
    },
    diretoriaLabel() {
      return contextoEstadual.diretoria ? contextoEstadual.diretoria.label : "";
    },
    aguardandoEstadual() {
      return !contextoEstadual.carregado;
    },
  },
  watch: {
    // O Estadual aparece depois que a diretoria chega da API: carrega os cards novos.
    visiveis() {
      this.enfileirar();
    },
  },
  created() {
    this.vivo = true;
    this.timers = [];
    this.fila = [];
    this.ativos = 0;
    carregarContextoEstadual();
    this.enfileirar();
  },
  beforeDestroy() {
    this.vivo = false;
    this.timers.forEach((t) => clearTimeout(t));
  },
  methods: {
    iniciais,
    fmtPeriodo,
    fmtValor(ranking, v) {
      return fmtIndicador(v, ranking.fmt);
    },
    card(id) {
      return this.cards[id] || { estado: "carregando", dados: null };
    },
    enfileirar() {
      this.visiveis.forEach((r) => {
        if (this.cards[r.id]) return;
        this.$set(this.cards, r.id, { estado: "carregando", dados: null });
        this.fila.push(r);
      });
      this.proximo();
    },
    proximo() {
      while (this.vivo && this.ativos < SIMULTANEOS && this.fila.length) {
        this.ativos++;
        this.buscar(this.fila.shift(), 0);
      }
    },
    terminar(id, estado, dados) {
      this.$set(this.cards, id, { estado, dados });
      this.ativos--;
      this.proximo();
    },
    recarregar(ranking) {
      this.$set(this.cards, ranking.id, { estado: "carregando", dados: null });
      this.ativos++;
      this.buscar(ranking, 0);
    },
    buscar(ranking, tentativa) {
      api
        .get(ranking.endpoint)
        .then((res) => {
          if (!this.vivo) return;
          const corpo = (res && res.data) || {};
          if (res.status === 202 || corpo.status === "processing") {
            if (tentativa >= POLL_MAX_TENTATIVAS) return this.terminar(ranking.id, "erro", null);
            this.$set(this.cards, ranking.id, { estado: "processando", dados: null });
            this.timers.push(setTimeout(() => this.buscar(ranking, tentativa + 1), POLL_INTERVALO_MS));
            return;
          }
          if (corpo.status === "success") return this.terminar(ranking.id, "ok", corpo);
          if (corpo.status === "sem_diretoria") return this.terminar(ranking.id, "sem_diretoria", null);
          this.terminar(ranking.id, "erro", null);
        })
        .catch(() => {
          if (this.vivo) this.terminar(ranking.id, "erro", null);
        });
    },
    podio(dados) {
      return ((dados && dados.top) || []).slice(0, 3);
    },
    euRanqueado(dados) {
      const eu = dados && dados.eu;
      return !!(eu && eu.participa && !eu.sem_resultado);
    },
  },
};
</script>

<template>
  <Layout>
    <div class="rk-page">
      <ol class="breadcrumb">
        <li class="breadcrumb-item">
          <router-link tag="a" to="/" aria-label="Início">
            <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
              <path d="M5 22h14a2 2 0 0 0 2-2v-9a1 1 0 0 0-.29-.71l-8-8a1 1 0 0 0-1.41 0l-8 8A1 1 0 0 0 3 11v9a2 2 0 0 0 2 2zm5-2v-5h4v5zm-5-8.59 7-7 7 7V20h-3v-5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v5H5z"></path>
            </svg>
          </router-link>
        </li>
        <li class="breadcrumb-item">Rankings</li>
        <li class="breadcrumb-item">Resumo</li>
      </ol>

      <div class="page-title-box mb-3">
        <h3 class="mb-1">Resumo dos Rankings</h3>
        <p class="rk-subtitulo mb-0">O pódio e a sua posição em cada ranking do mês. Toque no card para ver o ranking completo.</p>
      </div>

      <div v-if="!secoes.length && !aguardandoEstadual" class="card">
        <div class="card-body text-center rs-vazio">Nenhum ranking liberado para o seu usuário.</div>
      </div>

      <section v-for="secao in secoes" :key="secao.key" class="rs-secao" :aria-labelledby="'rs-' + secao.key">
        <h2 :id="'rs-' + secao.key" class="rs-secao-titulo">
          {{ secao.label }}
          <small v-if="secao.key === 'estadual' && diretoriaLabel" class="rs-diretoria">
            <i class="bx bx-map-pin" aria-hidden="true"></i> {{ diretoriaLabel }}
          </small>
        </h2>

        <div v-for="grupo in secao.grupos" :key="grupo.key" class="rs-grupo">
          <h3 class="rs-grupo-titulo">{{ grupo.label }}</h3>
          <div class="rs-grid">
            <article v-for="r in grupo.itens" :key="r.id" class="card rs-card">
              <div class="card-body">
                <h4 class="rs-card-titulo">{{ r.menuLabel }}</h4>

                <div v-if="card(r.id).estado === 'carregando' || card(r.id).estado === 'processando'" class="rs-carregando" role="status">
                  <b-spinner small class="mr-2" />
                  {{ card(r.id).estado === "processando" ? "Calculando…" : "Carregando…" }}
                </div>

                <div v-else-if="card(r.id).estado === 'sem_diretoria'" class="rs-aviso">
                  Seu cadastro não está vinculado a nenhuma diretoria.
                </div>

                <div v-else-if="card(r.id).estado === 'erro'" class="rs-aviso">
                  Não foi possível carregar.
                  <b-button size="sm" variant="link" class="p-0 align-baseline" @click="recarregar(r)">Tentar de novo</b-button>
                </div>

                <template v-else>
                  <p class="rs-periodo">
                    <i :class="card(r.id).dados.fechado ? 'bx bx-lock-alt' : 'bx bx-time-five'" aria-hidden="true"></i>
                    {{ card(r.id).dados.fechado ? "Resultado oficial" : "Parcial" }} · {{ fmtPeriodo(card(r.id).dados.periodo) }}
                  </p>

                  <ol v-if="podio(card(r.id).dados).length" class="rs-podio">
                    <li v-for="item in podio(card(r.id).dados)" :key="item.posicao" :class="{ 'is-eu': item.eu }">
                      <span class="rs-medalha" aria-hidden="true">{{ MEDALHAS[item.posicao] }}</span>
                      <span class="sr-only">{{ item.posicao }}º lugar:</span>
                      <img v-if="item.foto" :src="item.foto" class="rk-mini rs-foto" alt="" loading="lazy" />
                      <span v-else class="rk-mini" aria-hidden="true">{{ iniciais(item.nome) }}</span>
                      <span class="rs-nome">{{ item.nome }}<span v-if="item.eu" class="rk-selo-voce">Você</span></span>
                      <span class="rs-valor">{{ fmtValor(r, item.valor) }}</span>
                    </li>
                  </ol>
                  <p v-else class="rs-aviso">Ainda sem resultados neste período.</p>

                  <div class="rs-eu">
                    <template v-if="euRanqueado(card(r.id).dados)">
                      <span class="rs-eu-pos">{{ card(r.id).dados.eu.posicao }}º</span>
                      <span>
                        Sua posição de {{ card(r.id).dados.eu.total_participantes }} ·
                        <strong>{{ fmtValor(r, card(r.id).dados.eu.valor) }}</strong> {{ r.metricLabel.toLowerCase() }}
                      </span>
                    </template>
                    <span v-else-if="card(r.id).dados.eu && card(r.id).dados.eu.participa">Você ainda não tem resultado neste período.</span>
                    <span v-else>Você não participa deste ranking.</span>
                  </div>
                </template>

                <router-link :to="r.path" class="rs-link">
                  Ver ranking completo <i class="bx bx-chevron-right" aria-hidden="true"></i>
                </router-link>
              </div>
            </article>
          </div>
        </div>
      </section>
    </div>
  </Layout>
</template>

<style scoped src="./ranking.css"></style>

<style scoped>
.rs-vazio { color: var(--st-text-muted); }
.rs-secao { margin-bottom: 24px; }
.rs-secao-titulo {
  display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px;
  font-size: 1.25rem; font-weight: 700; color: var(--st-text); margin: 0 0 12px;
}
.rs-diretoria { font-size: 13px; font-weight: 600; color: var(--st-text-muted); }
.rs-grupo + .rs-grupo { margin-top: 16px; }
.rs-grupo-titulo {
  font-size: 13px; font-weight: 700; letter-spacing: 0.06em; text-transform: uppercase;
  color: var(--st-brand); margin: 0 0 10px;
}
.rs-grid {
  display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 16px;
}
.rs-card { margin-bottom: 0; height: 100%; }
.rs-card .card-body { display: flex; flex-direction: column; }
.rs-card-titulo { font-size: 15px; font-weight: 700; color: var(--st-text); margin: 0 0 6px; }
.rs-periodo { font-size: 12px; color: var(--st-text-muted); margin: 0 0 10px; }
.rs-carregando, .rs-aviso { font-size: 13px; color: var(--st-text-muted); margin: 8px 0 12px; }
.rs-podio { list-style: none; padding: 0; margin: 0 0 12px; }
.rs-podio li {
  display: flex; align-items: center; gap: 8px; padding: 6px 4px;
  border-bottom: 1px solid var(--st-border);
}
.rs-podio li:last-child { border-bottom: 0; }
.rs-podio li.is-eu { background: var(--st-brand-soft); border-radius: var(--st-radius); }
.rs-medalha { width: 22px; text-align: center; font-size: 18px; flex-shrink: 0; }
.rs-foto { object-fit: cover; }
.rs-nome {
  flex: 1 1 auto; min-width: 0; font-size: 13px; font-weight: 600; color: var(--st-text);
  overflow: hidden; text-overflow: ellipsis; white-space: nowrap;
}
.rs-valor { font-size: 13px; font-weight: 700; color: var(--st-text); white-space: nowrap; }
.rs-eu {
  display: flex; align-items: center; gap: 10px; padding: 8px 10px;
  border-radius: var(--st-radius); background: var(--st-canvas);
  font-size: 13px; color: var(--st-text);
}
.rs-eu-pos { font-size: 20px; font-weight: 800; color: var(--st-brand); }
.rs-link {
  display: inline-flex; align-items: center; gap: 2px; margin-top: auto; padding-top: 12px;
  font-size: 0.875rem; font-weight: 600;
}
@media (max-width: 575.98px) {
  .rs-grid { grid-template-columns: minmax(0, 1fr); }
}
</style>
